import React, {useEffect, useRef, useState, forwardRef} from 'react';
import PropTypes from 'prop-types';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import { t } from 'i18next';

export let mounted = [];
let filterTimeChanged = new Date().getTime();
export const DataTableLazy = ({
    id,
    className,
    selectionMode,
    rendered,
    filterDisplay,
    initialCriteria,
    headerStyle,
    columns,
    rows,
    rowsPerPageOptions,
    onSelect,
    dataPageable,
    loadLazyData,
    paginator,
    fetchReturnResolve,
}) => {
    const [size, setSize] = useState(dataPageable?.totalElements);
    const [list, setList] = useState(dataPageable?.content);
    const [loading, setLoading] = useState(false);
    const [selectedElements, setSelectedElements] = useState([]);
    const [filters, setFilters] = useState({});
    const [criteria, setCriteria] = useState(initialCriteria);
    const [tableRows, setTableRows] = useState(rows);
    let loadLazyTimeout = useRef(null);
    useEffect(() => {
        const el = mounted.find((el) => el === id);
        if (filterDisplay === 'menu') {
            setList(dataPageable?.content);
            setSize(dataPageable?.totalElements);
        }
        if (!el) {
            mounted.push(id);
            createAndSetFilters(columns);
        }
    }, [criteria, filterDisplay, id, loadLazyData, columns, dataPageable]);

    useEffect(() => {
        setTableRows(rows);
    }, [rows]);

    if (!rendered) {
        return;
    }

    const onPage = (event) => {
        const criteriaToRequest = structuredClone(criteria);
        const firstResult = event.first;
        criteriaToRequest.firstResult = firstResult;
        criteriaToRequest.maxResults = event.rows;
        criteria.firstResult = event.first;
        criteria.maxResults = event.rows;
        setCriteria(criteria);
        if (event.rows && event.rows !== tableRows) {
            setTableRows(event.rows);
        }
        setLoading(true);
        if (fetchReturnResolve) {
            loadLazyData(criteriaToRequest).then((res) => {
                setList(res?.content);
                setSize(res?.totalElements);
                setLoading(false);
            });
        } else {
            loadLazyData(criteriaToRequest);
            setLoading(false);
        }
    };

    const onFilter = (event) => {
        const filters = event?.filters;
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                criteria[key] = value?.value ? value?.value : '';
                filters[key] = {value: value?.value ? value?.value : '', matchMode: 'contains'};
            });
            setCriteria(criteria);
            setFilters(filters);
            loadLazyDataWithDelayOnKeyDown(criteria);
        }
    };

    const loadLazyDataWithDelayOnKeyDown = (criteriaToRequest) => {
        const DELAY_MILISECONDS = 1200;
        const currentTime = new Date().getTime();
        filterTimeChanged = currentTime;
        loadLazyTimeout.current = setTimeout(() => {
            const currentTime = new Date().getTime();
            if (currentTime - filterTimeChanged < DELAY_MILISECONDS) {
                return;
            }
            setLoading(true);
            loadLazyData(criteriaToRequest).then((res) => {
                setList(res?.content);
                setSize(res?.totalElements);
                setLoading(false);
            });
        }, DELAY_MILISECONDS);
    };

    const createAndSetFilters = (columns) => {
        const newFilters = {};
        columns.forEach((column) => {
            if (column.filter) {
                newFilters[column.field] = {value: null, matchMode: 'contains'};
            }
        });
        setFilters(newFilters);
    };

    const onSelectionChange = (event) => {
        let value = event.value;
        if (value === undefined) {
            setSelectedElements([]);
            value = [];
        }
        if (selectedElements[0] === value[0]) {
            setSelectedElements([]);
            value = [];
        } else {
            setSelectedElements(value);
        }
        onSelect(value[0]);
    };
    const getFloatStyle = (col) => {
        let bodyStyle = col?.bodyStyle;
        if (col.float) {
            bodyStyle = {
                textAlign: col.float,
            };
        }
        return bodyStyle;
    };
    const dynamicColumns = columns.map((col, i) => {
        return (
            <Column
                key={col.field}
                field={col.field}
                header={col.header}
                style={{minWidth: col.minWidth}}
                filter={col.filter}
                sortable={col.sortable}
                filterConstraint={false}
                body={col.body}
                headerStyle={getFloatStyle(col)}
                bodyStyle={getFloatStyle(col)}
            />
        );
    });

    return (
        <DataTable
            id={id}
            className={`custom-lazy-data-table ${className} `}
            value={list}
            lazy
            filterDisplay={filterDisplay}
            responsiveLayout='scroll'
            dataKey='id'
            paginator={paginator}
            selectionMode={selectionMode}
            rows={tableRows}
            rowsPerPageOptions={rowsPerPageOptions}
            first={criteria.firstResult}
            sortField={criteria.sortField}
            sortOrder={criteria.sortAsc}
            onFilter={onFilter}
            emptyMessage={t('global.empty-list')}
            loading={loading}
            filters={filters}
            headerStyle={headerStyle}
            totalRecords={size}
            onPage={onPage}
            selectAll={false}
            selection={selectedElements}
            onSelectionChange={onSelectionChange}
        >
            {dynamicColumns}
        </DataTable>
    );
};

DataTableLazy.defaultProps = {
    className: 'col-xl-12 col-lg-12 col-md-12 col-sm-12',
    selectionMode: undefined,
    rendered: true,
    filterDisplay: 'row',
    headerStyle: {width: '0em'},
    columns: [{}],
    rows: 10,
    rowsPerPageOptions: [10, 20, 50],
    paginator: true,
    fetchReturnResolve: true,
};

DataTableLazy.propTypes = {
    className: PropTypes.string,
    selectionMode: PropTypes.string,
    filterDisplay: PropTypes.string,
    rendered: PropTypes.bool,
    paginator: PropTypes.bool,
    fetchReturnResolve: PropTypes.bool,
    data: PropTypes.object.isRequired,
    criteria: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    loadLazyData: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    headerStyle: PropTypes.object,
    columns: PropTypes.array,
    rows: PropTypes.number,
    rowsPerPageOptions: PropTypes.array,
};

export default DataTableLazy;
